/**
 * Ticket 114.3 — Tenant Admin brand defaults page for the Demos module.
 *
 * Requires demo.template.manage permission (enforced via ProtectedRoute).
 *
 * Fields: primary brand color, secondary brand color, logo URL, allowed themes
 * (multi-select from the 4 themes).
 *
 * Stored in tenant_demo_brand_defaults (V154 migration) via
 * GET/PUT /api/v1/demos/brand-defaults.
 *
 * Route: /admin/demos/brand-defaults
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  getDemoBrandDefaults,
  updateDemoBrandDefaults,
  type DemoTheme,
} from '@/api/demos';

const ALL_THEMES: DemoTheme[] = ['sunburst', 'editorial', 'quantum', 'aurora'];

function parseAllowedThemes(json: string): DemoTheme[] {
  try {
    return JSON.parse(json) as DemoTheme[];
  } catch {
    return [...ALL_THEMES];
  }
}

export function DemoBrandDefaultsPage() {
  const { t } = useTranslation();

  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [allowedThemes, setAllowedThemes] = useState<DemoTheme[]>([...ALL_THEMES]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getDemoBrandDefaults()
      .then((d) => {
        setPrimaryColor(d.primaryColor ?? '');
        setSecondaryColor(d.secondaryColor ?? '');
        setLogoUrl(d.logoUrl ?? '');
        setAllowedThemes(parseAllowedThemes(d.allowedThemes));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function toggleTheme(th: DemoTheme) {
    setAllowedThemes((prev) =>
      prev.includes(th) ? prev.filter((t) => t !== th) : [...prev, th],
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateDemoBrandDefaults({
        primaryColor: primaryColor || null,
        secondaryColor: secondaryColor || null,
        logoUrl: logoUrl || null,
        allowedThemes,
      });
      showToast(t('demos.admin.brand.success'));
    } catch {
      showToast(t('demos.admin.brand.error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  return (
    <div className="p-6 max-w-xl mx-auto space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-background border border-border rounded-md px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      <h1 className="text-2xl font-semibold">{t('demos.admin.brand.title')}</h1>

      <div className="space-y-4">
        {/* Primary color */}
        <div className="space-y-1">
          <label className="text-sm font-medium" htmlFor="brand-primary">
            {t('demos.admin.brand.primaryColor')}
          </label>
          <div className="flex gap-2 items-center">
            <input
              id="brand-primary-swatch"
              type="color"
              value={primaryColor || '#000000'}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="h-9 w-9 rounded border border-input cursor-pointer bg-transparent p-0.5"
              aria-label={t('demos.admin.brand.primaryColor')}
            />
            <Input
              id="brand-primary"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#RRGGBB"
              className="flex-1"
            />
          </div>
        </div>

        {/* Secondary color */}
        <div className="space-y-1">
          <label className="text-sm font-medium" htmlFor="brand-secondary">
            {t('demos.admin.brand.secondaryColor')}
          </label>
          <div className="flex gap-2 items-center">
            <input
              id="brand-secondary-swatch"
              type="color"
              value={secondaryColor || '#000000'}
              onChange={(e) => setSecondaryColor(e.target.value)}
              className="h-9 w-9 rounded border border-input cursor-pointer bg-transparent p-0.5"
              aria-label={t('demos.admin.brand.secondaryColor')}
            />
            <Input
              id="brand-secondary"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              placeholder="#RRGGBB"
              className="flex-1"
            />
          </div>
        </div>

        {/* Logo URL */}
        <div className="space-y-1">
          <label className="text-sm font-medium" htmlFor="brand-logo">
            {t('demos.admin.brand.logo')}
          </label>
          <Input
            id="brand-logo"
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://…"
          />
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo preview"
              className="mt-2 h-12 w-auto rounded border border-border object-contain"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          )}
        </div>

        {/* Allowed themes multi-select */}
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('demos.admin.brand.allowedThemes')}</p>
          <div className="flex flex-wrap gap-2">
            {ALL_THEMES.map((th) => {
              const isSelected = allowedThemes.includes(th);
              return (
                <button
                  key={th}
                  type="button"
                  onClick={() => toggleTheme(th)}
                  aria-pressed={isSelected}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors
                    ${isSelected
                      ? 'border-[color:var(--color-lop-teal)] bg-[color:var(--color-lop-teal)]/10 text-[color:var(--color-lop-teal)]'
                      : 'border-border text-muted-foreground'}`}
                >
                  {t(`demos.theme.${th}.label`)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving}>
        {saving ? '…' : t('demos.admin.brand.save')}
      </Button>
    </div>
  );
}
